<script setup lang="ts">
  import { useToast } from '@bv/composables/common/toast';
  import { trans } from '@bv/utils';
  import { useVuelidate } from '@vuelidate/core';
  import { email, helpers, maxLength, minLength, required } from '@vuelidate/validators';
  import { ref, computed, reactive } from 'vue';
  import { useRoute } from 'vue-router';

  import { contactsApi } from '@/api/webphone/contacts';
  import type { ContactDetailsData } from '@/types/api/webPhone/nestedViews/Contact';

  defineOptions({ name: 'ContactDialog' });

  const emit = defineEmits<{
    (event: 'contact-added', contact: any): void;
    (event: 'contact-updated', contact: ContactDetailsData): void;
  }>();

  const { editData } = defineProps<{
    editData?: ContactDetailsData;
  }>();

  const showDialog = defineModel<boolean>();

  const route = useRoute();
  const toast = useToast();

  const isCreating = ref(false);
  const phoneValid = ref(false);
  const avatarFile = ref<File | null>(null);
  const avatarPreview = ref<string | null>(null);
  const fileInputRef = ref<HTMLInputElement | null>(null);

  const formData = reactive({
    fullName: '',
    phoneNumber: '',
    emailAddress: '',
    companyName: '',
  });

  const isEditMode = computed(() => !!editData);

  const rules = {
    fullName: {
      required: helpers.withMessage(trans('validations.required'), required),
      minLength: helpers.withMessage(trans('validations.minLength'), minLength(2)),
      maxLength: helpers.withMessage(trans('validations.maxLength'), maxLength(100)),
    },
    phoneNumber: {
      required: helpers.withMessage(trans('validations.required'), required),
    },
    emailAddress: { email: helpers.withMessage(trans('validations.email'), email) },
    companyName: {},
  };

  const v$ = useVuelidate(rules, formData);

  const modalTitle = computed(() => {
    return !isEditMode.value
      ? trans('call_center.contact_form.add_contact')
      : trans('call_center.contact_form.edit_contact');
  });

  const isValid = computed(() => !v$.value.$invalid && phoneValid.value);

  const submitType = async (data: FormData) => {
    if (isEditMode.value) {
      return await contactsApi
        .update(route.params.contactId as string, data)
        .then((contact: ContactDetailsData) => {
          showDialog.value = false;
          toast.success(trans('call_center.contact_details.toast.updated'));
          emit('contact-updated', contact);
        })
        .finally(() => {
          isCreating.value = false;
        });
    } else {
      return await contactsApi
        .create(data)
        .then((res) => {
          showDialog.value = false;
          emit('contact-added', res);
          toast.success(trans('call_center.contact_details.toast.created'));
        })
        .finally(() => {
          isCreating.value = false;
        });
    }
  };

  const onAvatarClick = () => {
    fileInputRef.value?.click();
  };

  const onAvatarChange = (event: Event) => {
    const target = event.target as HTMLInputElement;
    const file = target.files?.[0];
    if (file) {
      avatarFile.value = file;
      avatarPreview.value = URL.createObjectURL(file);
    }
  };

  const handleAddNewContact = async () => {
    v$.value.$touch();
    if (!v$.value.$invalid) {
      isCreating.value = true;
      const data = new FormData();
      data.append('full_name', formData.fullName);
      data.append('phone_number', formData.phoneNumber);
      data.append('company_name', formData.companyName);
      data.append('source', 'import');
      data.append('type', 'internal');
      data.append('remove_avatar', '0');
      if (formData.emailAddress) {
        data.append('email', formData.emailAddress);
      }
      if (avatarFile.value) {
        data.append('avatar', avatarFile.value);
      }

      await submitType(data);
    }
  };

  const resetForm = () => {
    formData.fullName = '';
    formData.phoneNumber = '';
    formData.emailAddress = '';
    formData.companyName = '';
    v$.value.$reset();
    phoneValid.value = false;
    isCreating.value = false;
    avatarFile.value = null;
    avatarPreview.value = null;
  };

  const handelCloseModal = () => {
    if (isEditMode.value) {
      return;
    }
    resetForm();
  };

  // created hook — initialize from editData prop
  if (editData) {
    formData.fullName = editData.full_name;
    formData.phoneNumber = editData.phone_number as string;
    formData.emailAddress = editData.email as string;
    formData.companyName = editData.company_name as string;
    if (editData.avatar) {
      avatarPreview.value = editData.avatar;
    }
  }
</script>

<template>
  <bv-modal
    v-model="showDialog"
    :cancel-text="trans('global.form.cancel')"
    :confirm-text="trans('global.form.submit')"
    :disable-cancel="isCreating"
    :disable-confirm="!isValid"
    :loading-confirm="isCreating"
    :persistent="isCreating"
    :title="modalTitle"
    @close="handelCloseModal"
    @confirm="handleAddNewContact"
  >
    <div class="grid gap-6">
      <div
        class="flex h-30 w-full cursor-pointer items-center justify-center rounded-2xl border border-gray-200"
        @click="onAvatarClick"
      >
        <bv-avatar
          :image="avatarPreview"
          :name="formData.fullName"
        />
        <input
          ref="fileInputRef"
          accept="image/*"
          class="hidden"
          type="file"
          @change="onAvatarChange"
        />
      </div>
      <bv-text-input
        v-model="v$.fullName.$model"
        :error="v$.fullName.$error"
        :error-messages="v$.fullName.$errors"
        :label="trans('call_center.contact_form.full_name')"
        :placeholder="trans('call_center.contact_form.full_name_placeholder')"
        required
      />
      <bv-tel-input
        v-model="v$.phoneNumber.$model"
        :error="v$.phoneNumber.$error"
        :error-messages="v$.phoneNumber.$errors[0]?.$message"
        :label="trans('call_center.contact_form.phone_number')"
        :placeholder="trans('call_center.contact_form.phone_number_placeholder')"
        required
        @update:is-valid="phoneValid = $event"
      />
      <bv-text-input
        v-model="v$.emailAddress.$model"
        :error="v$.emailAddress.$error"
        :error-messages="v$.emailAddress.$errors"
        :label="trans('call_center.contact_form.email')"
        :placeholder="trans('call_center.contact_form.email_placeholder')"
      />
      <bv-text-input
        v-model="v$.companyName.$model"
        :error="v$.companyName.$error"
        :error-messages="v$.companyName.$errors"
        :label="trans('call_center.contact_form.company')"
        :placeholder="trans('call_center.contact_form.company_placeholder')"
      />
    </div>
  </bv-modal>
</template>
